import React, { useState, useEffect, useContext, useCallback, useRef } from 'react'
import { FormattedMessage } from 'react-intl'
import { PageTitle, PageSubtitle, TooltipHintWrapper } from 'flow/siteProposal/pages/about/style'
import Tooltip from 'components/Tooltip'
import InfoIcon from 'assets/icons/info-icon'
import ScrollToTop from 'components/ScrollToTop'
import BiodiversitySelector from 'components/BiodiversitySelector'
import StateEvaluationSelector from './CreateEvaluation/StateEvaluationSelector'
import { useParams } from 'react-router-dom'
import { MonitoringContext } from 'providers/monitoring'
import { showErrorToast } from 'utils/toast'
import {
  createBulkTriggerStateBenchmark,
  monitoringStateBio,
  requestCreateSiteState,
  requestTriggerStateBenchmark,
  requestTriggerStateBenchmarkTaxonGroups
} from 'api/monitoring'
import { ErrorWrapper, PageSubtitleWrapper } from './style'
import ErrorIcon from 'assets/icons/error-input-trailing-icon'
import Button from 'components/Button'
import Modal from 'components/Modal'
import { RemoveItemModalFooter, RemoveItemModalText } from 'flow/SiteMonitoring/pages/Pressure/component/style'
import Loader from 'components/Loader'
import LoadingWrapper from 'components/LoadingWrapper'

const SiteMonitoringStatePage = () => {
  const { id: siteId, monitoringId } = useParams()
  const {
    siteStateRecord,
    setSiteStateRecord,
    biodiversityFilter,
    setBiodiversityFilter,
    monitoringProposalFieldValidation,
    isEditable
  } = useContext(MonitoringContext)

  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [isRevaluation, setIsRevaluation] = useState(false)
  const [initialLoad, setInitialLoad] = useState(true)
  const isFirstFetchRef = useRef(true)
  const [activeTab, setActiveTab] = useState('biodiversity')
  const filterBy = biodiversityFilter
  const [biodiversityPagination, setBiodiversityPagination] = useState({
    resultsPerPage: 12,
    totalNumber: 0,
    totalPages: 1,
    currentPage: 1,
    hasNext: false,
    hasPrevious: false
  })
  const prevFilterRef = useRef(filterBy)
  const incompleteBenchmarkIds = monitoringProposalFieldValidation?.siteState?.benchmarkIds
  const isSiteStateValid = monitoringProposalFieldValidation?.siteState?.result
  const [showTabWarning, setShowTabWarning] = useState(false)

  const [selectedByFilter, setSelectedByFilter] = useState({
    1: [],
    2: []
  })
  const [selectedGroupsByFilter, setSelectedGroupsByFilter] = useState({
    1: [],
    2: []
  })
  // tracking select-all per page
  const [selectedAllMap, setSelectedAllMap] = useState({})
  const [globalSelectAll, setGlobalSelectAll] = useState(false)
  const [totalBenchmarks, setTotalBenchmarks] = useState(0)
  const [isAllAdded, setIsAllAdded] = useState(false)

  const selected = selectedByFilter[filterBy] || []

  const goBackToBiodiversity = async () => {
    setActiveTab('biodiversity')
    backendSyncedRef.current = {
      ...backendSyncedRef.current,
      [biodiversityFilter]: false
    }

    setBiodiversityPagination((prev) => ({ ...prev, currentPage: 1 }))

    try {
      await fetchData(1)
    } catch (err) {
      console.error('[SiteState] Failed to refresh biodiversity on back', err)
    }
  }

  const getCurrentPageIds = useCallback(() => {
    return items.map((it) => it.raw.historyId || it.raw.id)
  }, [items])

  const getPageKey = (page = biodiversityPagination.currentPage, filter = filterBy) => `${filter}-${page}`

  const allItemsRef = useRef(new Map())
  const backendSyncedRef = useRef({})

  useEffect(() => {
    if (!items.length) return

    const newItems = []
    items.forEach((item) => {
      const key = item.raw.historyId || item.raw.id
      if (!allItemsRef.current.has(key)) {
        newItems.push(item)
      }
      allItemsRef.current.set(key, item)
    })

    const currentPageIds = items.map((item) => item.raw.historyId || item.raw.id)
    const backendCheckedIds = items.filter((item) => item.raw.isChecked).map((item) => item.raw.historyId || item.raw.id)

    const currentGroups = selectedGroupsByFilter[filterBy] || []
    const currentSelectedSet = new Set(selectedByFilter[filterBy] || [])
    let hasAutoSelectedChanges = false

    newItems.forEach((item) => {
      if (item.className && currentGroups.includes(item.className)) {
        const id = item.raw.historyId || item.raw.id
        if (!currentSelectedSet.has(id)) {
          currentSelectedSet.add(id)
          hasAutoSelectedChanges = true
        }
      }
    })

    if (hasAutoSelectedChanges) {
      setSelectedByFilter((prev) => ({
        ...prev,
        [filterBy]: Array.from(currentSelectedSet)
      }))
    }

    const effectiveSelectedSet = new Set([...Array.from(currentSelectedSet), ...backendCheckedIds])

    const allPageItemsSelected = currentPageIds.length > 0 && currentPageIds.every((id) => effectiveSelectedSet.has(id))

    setSelectedAllMap((prev) => ({
      ...prev,
      [getPageKey(biodiversityPagination.currentPage, filterBy)]: allPageItemsSelected
    }))
  }, [items, biodiversityPagination.currentPage, selectedGroupsByFilter, filterBy])

  const handleSelectAll = async (e) => {
    const checked = e.target.checked
    const pageIds = getCurrentPageIds()
    const currentPage = biodiversityPagination.currentPage

    const classNamesOnPage = new Set(items.map((it) => it.className).filter(Boolean))
    setSelectedGroupsByFilter((prevGrp) => {
      const grps = new Set(prevGrp[filterBy] || [])
      if (checked) {
        classNamesOnPage.forEach((cls) => grps.add(cls))
      } else {
        classNamesOnPage.forEach((cls) => grps.delete(cls))
      }
      return { ...prevGrp, [filterBy]: Array.from(grps) }
    })

    setSelectedByFilter((prev) => {
      const current = new Set(prev[filterBy] || [])

      if (checked) {
        pageIds.forEach((id) => current.add(id))
        classNamesOnPage.forEach((cls) => {
          Array.from(allItemsRef.current.values()).forEach((it) => {
            if (it.className === cls) {
              current.add(it.raw.historyId || it.raw.id)
            }
          })
        })
      } else {
        pageIds.forEach((id) => current.delete(id))
        classNamesOnPage.forEach((cls) => {
          Array.from(allItemsRef.current.values()).forEach((it) => {
            if (it.className === cls) {
              current.delete(it.raw.historyId || it.raw.id)
            }
          })
        })
      }

      return {
        ...prev,
        [filterBy]: Array.from(current)
      }
    })

    setSelectedAllMap((prev) => ({ ...prev, [getPageKey(currentPage, filterBy)]: checked }))
  }

  const clearAllSelections = () => {
    setSelectedByFilter({})
    setSelectedAllMap({})
    setSelectedGroupsByFilter({ 1: [], 2: [] })
  }

  const handleToggle = (item) => {
    const id = item.raw.historyId || item.raw.id
    const currentPage = biodiversityPagination.currentPage
    const className = item.className

    setSelectedByFilter((prev) => {
      const current = new Set(prev[filterBy] || [])

      if (current.has(id)) current.delete(id)
      else current.add(id)

      if (className) {
        const classItems = items.filter((it) => it.className === className)
        const allSelected = classItems.every((it) => {
          const itId = it.raw.historyId || it.raw.id
          return current.has(itId) || it.raw.isChecked
        })

        setSelectedGroupsByFilter((prevGrp) => {
          const grps = new Set(prevGrp[filterBy] || [])
          if (allSelected) {
            grps.add(className)
          }
          return { ...prevGrp, [filterBy]: Array.from(grps) }
        })
      }

      // updating selectall for current page
      const pageIds = getCurrentPageIds()
      const allPageSelected = pageIds.length > 0 && pageIds.every((pid) => current.has(pid))

      setSelectedAllMap((prev) => ({ ...prev, [getPageKey(currentPage, filterBy)]: allPageSelected }))

      return {
        ...prev,
        [filterBy]: Array.from(current)
      }
    })
  }

  const handleToggleClass = async (className, checked) => {
    const currentPage = biodiversityPagination.currentPage

    setSelectedGroupsByFilter((prevGrp) => {
      const grps = new Set(prevGrp[filterBy] || [])
      if (checked) {
        grps.add(className)
      } else {
        grps.delete(className)
      }
      return { ...prevGrp, [filterBy]: Array.from(grps) }
    })

    setSelectedByFilter((prev) => {
      const current = new Set(prev[filterBy] || [])

      if (checked) {
        Array.from(allItemsRef.current.values()).forEach((it) => {
          if (it.className === className) {
            current.add(it.raw.historyId || it.raw.id)
          }
        })
      } else {
        Array.from(allItemsRef.current.values()).forEach((it) => {
          if (it.className === className) {
            current.delete(it.raw.historyId || it.raw.id)
          }
        })
      }

      const pageIds = getCurrentPageIds()
      const allPageSelected = pageIds.length > 0 && pageIds.every((pid) => current.has(pid))

      setSelectedAllMap((prev) => ({ ...prev, [getPageKey(currentPage, filterBy)]: allPageSelected }))

      return {
        ...prev,
        [filterBy]: Array.from(current)
      }
    })
  }

  const isClassSelected = (className) => {
    const classItems = items.filter((it) => it.className === className)
    if (!classItems.length) return false

    return classItems.every((it) => {
      const id = it.raw.historyId || it.raw.id
      return selected.includes(id) || it.raw.isChecked
    })
  }
  const isClassDisabled = (className) =>
    Array.from(allItemsRef.current.values())
      .filter((item) => item.type === 'Species' && item.className === className)
      .every((it) => it.raw?.isChecked)

  const isGlobalSelectDisabled =
    Array.from(allItemsRef.current.values())
      .filter((it) => it.type === (biodiversityFilter === 1 ? 'Species' : 'Ecosystem'))
      .every((it) => it.raw?.isChecked) || !isEditable

  const handleRemoveSelectedItem = (originalItemId) => {
    if (!originalItemId) return
    setSelectedByFilter((prev) => {
      const next = { ...prev }
      for (const key of Object.keys(next)) {
        next[key] = (next[key] || []).filter((id) => id !== originalItemId)
      }
      return next
    })

    const item = allItemsRef.current.get(originalItemId)
    if (item && item.className) {
      setSelectedGroupsByFilter((prev) => {
        const next = { ...prev }
        for (const key of Object.keys(next)) {
          next[key] = (next[key] || []).filter((cls) => cls !== item.className)
        }
        return next
      })
    }
  }

  const deriveItemsFromIds = useCallback(async (ids = []) => {
    const cachedItems = ids.map((id) => allItemsRef.current.get(id)).filter(Boolean)
    const missingIds = ids.filter((id) => !allItemsRef.current.get(id))

    if (missingIds.length === 0) {
      return cachedItems
    }

    // For missing IDs, we need to fetch them from the API
    // Since we don't have a direct API to get items by ID, we'll need to search through pages
    const fetchedItems = []
    let page = 1
    let hasMore = true
    const foundIds = new Set()

    while (hasMore && foundIds.size < missingIds.length) {
      try {
        const response = await monitoringStateBio({
          siteId,
          filterBy,
          page,
          limit: biodiversityPagination.resultsPerPage,
          monitoringId
        })

        const species = response?.siteSpecies || []

        for (const group of species) {
          for (const speciesItem of group.species || []) {
            const historyId = speciesItem.historyId
            if (missingIds.includes(historyId)) {
              foundIds.add(historyId)
              fetchedItems.push({
                type: 'Species',
                className: group.taxonomicGroup,
                raw: speciesItem
              })
            }
          }
        }

        // Check if there are more pages
        const apiPagination = response?.sitePagination
        if (apiPagination && apiPagination.totalPages > page) {
          page++
        } else {
          hasMore = false
        }
      } catch (error) {
        console.error('Error fetching missing items:', error)
        hasMore = false
      }
    }

    return [...cachedItems, ...fetchedItems]
  }, [])

  const fetchData = async (
    page = biodiversityPagination.currentPage,
    limit = biodiversityPagination.resultsPerPage,
    changeSelectAll = true
  ) => {
    setLoading(true)
    if (!siteId) {
      setLoading(false)
      return
    }
    try {
      const response = await monitoringStateBio({ siteId, filterBy, page, limit, monitoringId })
      const species = response?.siteSpecies || []
      const ecosystems = response?.siteEcoSystems || []

      const formattedItems = []
      if (filterBy === 1) {
        species.forEach((group) => {
          const className = group.taxonomicGroup

          group.species.forEach((item) => {
            formattedItems.push({
              id: item.historyId,
              name: item.scientificName,
              species: item.specieName,
              season: item.season,
              type: 'Species',
              className,
              raw: item
            })
          })
        })
      }

      if (filterBy === 2) {
        formattedItems.push(
          ...ecosystems.map((item) => ({
            id: item.id,
            name: item.name,
            type: 'Ecosystem',
            raw: item
          }))
        )
      }
      setTotalBenchmarks(response?.totalBenchmarks)
      setIsAllAdded(response?.isAllAdded)
      if (isFirstFetchRef.current) {
        const isEdit = response?.isReEvaluated === true && response?.totalBenchmarks > 0
        if (isEdit === true) {
          setIsRevaluation(true)
          setActiveTab('createEvaluation')
        }
        isFirstFetchRef.current = false
      }
      if (changeSelectAll) {
        if (response?.sitePagination?.resultsPerPage === response?.totalBenchmarks) {
          setSelectedAllMap((prev) => ({ ...prev, [getPageKey(page, filterBy)]: true }))
        } else {
          setSelectedAllMap((prev) => ({ ...prev, [getPageKey(page, filterBy)]: false }))
        }
      }
      setItems(formattedItems)
      const apiPagination = response?.sitePagination || null

      if (
        apiPagination &&
        (apiPagination.totalNumber || apiPagination.totalPages || apiPagination.total || apiPagination.count)
      ) {
        const resultsPerPage =
          apiPagination.resultsPerPage ?? apiPagination.limit ?? biodiversityPagination.resultsPerPage
        const totalNumber =
          apiPagination.totalNumber ?? apiPagination.total ?? apiPagination.count ?? biodiversityPagination.totalNumber
        const totalPages =
          apiPagination.totalPages ??
          (resultsPerPage ? Math.max(1, Math.ceil(totalNumber / resultsPerPage)) : biodiversityPagination.totalPages)
        const currentPage = apiPagination.currentPage ?? apiPagination.page ?? page
        setBiodiversityPagination({
          resultsPerPage,
          totalNumber,
          totalPages,
          currentPage,
          hasNext: apiPagination.hasNext ?? currentPage < totalPages,
          hasPrevious: apiPagination.hasPrevious ?? currentPage > 1
        })
      } else if (response?.sitePagination) {
        const api = response.sitePagination
        setBiodiversityPagination((prev) => ({
          resultsPerPage: api.resultsPerPage ?? prev.resultsPerPage,
          totalNumber: api.totalNumber ?? prev.totalNumber,
          totalPages: api.totalPages ?? prev.totalPages,
          currentPage: api.currentPage ?? page,
          hasNext: api.hasNext ?? false,
          hasPrevious: api.hasPrevious ?? false
        }))
      } else {
        setBiodiversityPagination((prev) => ({
          ...prev,
          totalNumber: formattedItems.length,
          totalPages: Math.max(1, Math.ceil(formattedItems.length / prev.resultsPerPage)),
          currentPage: page
        }))
      }
    } catch (err) {
      console.error('Failed to load biodiversity data', err)
      setItems([])
    } finally {
      setLoading(false)
      setInitialLoad(false)
    }
  }
  useEffect(() => {
    let currentPageToFetch = biodiversityPagination.currentPage
    if (prevFilterRef.current !== filterBy) {
      currentPageToFetch = 1
      prevFilterRef.current = filterBy
      setBiodiversityPagination((prev) => ({ ...prev, currentPage: 1 }))
    }
    if (siteId && monitoringId) {
      fetchData(currentPageToFetch)
    }
  }, [siteId, filterBy, monitoringId])

  const handleBiodiversitySelectPage = useCallback(
    (page) => {
      const newPage = Number(page) || 1
      fetchData(newPage, biodiversityPagination.resultsPerPage, false)
    },
    [biodiversityPagination.resultsPerPage, biodiversityPagination.currentPage, siteId, filterBy]
  )

  const handleBiodiversityNext = useCallback(() => {
    if (!biodiversityPagination.hasNext) return
    const next = (biodiversityPagination.currentPage || 1) + 1
    fetchData(next, biodiversityPagination.resultsPerPage, false)
  }, [
    biodiversityPagination.hasNext,
    biodiversityPagination.currentPage,
    biodiversityPagination.resultsPerPage,
    siteId,
    filterBy
  ])

  const handleBiodiversityPrev = useCallback(() => {
    if (!biodiversityPagination.hasPrevious) return
    const prev = Math.max(1, (biodiversityPagination.currentPage || 1) - 1)
    fetchData(prev, biodiversityPagination.resultsPerPage, false)
  }, [
    biodiversityPagination.hasPrevious,
    biodiversityPagination.currentPage,
    biodiversityPagination.resultsPerPage,
    siteId,
    filterBy
  ])

  const handleBiodiversityPerPageChange = useCallback(
    (option) => {
      const value = option?.value ?? Number(option) ?? biodiversityPagination.resultsPerPage
      setBiodiversityPagination((p) => ({ ...p, resultsPerPage: value, currentPage: 1 }))
      fetchData(1, value, false)
    },
    [biodiversityPagination.resultsPerPage, biodiversityPagination.currentPage, siteId, filterBy]
  )

  useEffect(() => {
    if (isEditable === false) {
      setActiveTab('createEvaluation')
    }
  }, [isEditable])

  useEffect(() => {
    const ensureSiteStateRecord = async () => {
      try {
        const postResponse = await requestCreateSiteState(monitoringId)
        if (postResponse?.id) {
          setSiteStateRecord(postResponse)
        } else {
          console.error('[SiteState] Failed to create new record.')
        }
      } catch (error) {
        console.error('[SiteState] ensureRecord failed', error)
      }
    }
    ensureSiteStateRecord()
  }, [])

  const handleFilterChange = (newFilterValue) => {
    if (typeof newFilterValue !== 'number') {
      console.warn('handleFilterChange expected number but got:', newFilterValue)
      return
    }

    const currentSelection = selectedByFilter[biodiversityFilter] || []
    if (currentSelection.length > 0) {
      setShowTabWarning(true)
      return
    }

    setGlobalSelectAll(false)
    setBiodiversityFilter(newFilterValue)
    setBiodiversityPagination((prev) => ({
      ...prev,
      currentPage: 1
    }))
  }

  const handleCreateEvaluation = useCallback(
    async (ids = [], isSelectedAll, activeTab, globalSelectAll) => {
      if (globalSelectAll) {
        try {
          await createBulkTriggerStateBenchmark(siteId, Number(siteStateRecord?.id), activeTab)
          setActiveTab('createEvaluation')
        } catch (error) {
          console.error('Bulk benchmark create error:', error)
          showErrorToast('Failed to create benchmark.')
        }
        return
      } else {
        if (!ids.length) {
          return showErrorToast('Please select at least one item')
        }
        const selection = await deriveItemsFromIds(ids)
        const alreadyBenchmarked = Array.from(allItemsRef.current.values()).filter(
          (it) =>
            it.type === (filterBy === 1 ? 'Species' : 'Ecosystem') &&
            Array.isArray(it?.raw?.triggerStateBenchmark) &&
            it.raw.triggerStateBenchmark.length > 0
        )

        const mergedMap = new Map()
        for (const it of [...selection, ...alreadyBenchmarked]) {
          const key = it.type === 'Species' ? it.raw?.historyId : it.raw?.id

          if (key) mergedMap.set(key, it)
        }
        const mergedSelection = Array.from(mergedMap.values())

        try {
          const newBenchmarksPayload = []
          const itemsToUpdate = []

          if (filterBy === 1) {
            // SPECIES
            const groupMap = new Map()
            for (const item of mergedSelection) {
              const hasBenchmark =
                Array.isArray(item?.raw?.triggerStateBenchmark) && item.raw.triggerStateBenchmark.length > 0
              if (!hasBenchmark && item.className) {
                if (!groupMap.has(item.className)) {
                  groupMap.set(item.className, {
                    items: [],
                    season: item.raw?.season?.id || item.raw?.season,
                    benchmark_estimate: item.raw?.benchmark_estimate || item.raw?.benchmarkEstimate
                  })
                }
                groupMap.get(item.className).items.push(item)
              }
            }

            const currentGroups = selectedGroupsByFilter[filterBy] || []

            const finalPayload = {
              site_state_id: Number(siteStateRecord.id),
              previous_parameter: false,
              justify_benchmark: '',
              class_names: []
            }

            for (const [className, groupData] of groupMap.entries()) {
              const isAllSpecies = currentGroups.includes(className)

              let groupPayload = {
                isAllSpecies: isAllSpecies
              }

              if (isAllSpecies) {
                const removed = []
                Array.from(allItemsRef.current.values()).forEach((it) => {
                  if (it.className === className && it.type === 'Species') {
                    const id = it.raw.historyId || it.raw.id
                    if (!ids.includes(id)) {
                      removed.push(id)
                    }
                  }
                })
                groupPayload.removed_species = removed
              } else {
                groupPayload.species = groupData.items.map((it) => it.raw.historyId || it.raw.id)
              }

              finalPayload.class_names.push({
                [className]: groupPayload
              })
            }

            let createdBenchmarks = []
            if (finalPayload.class_names.length > 0) {
              createdBenchmarks = await requestTriggerStateBenchmarkTaxonGroups(finalPayload)
            }

            if (createdBenchmarks.length > 0) {
              for (const benchmark of createdBenchmarks) {
                const itemKey = benchmark.siteSpeciesId || benchmark.siteEcosystemId
                if (itemKey && allItemsRef.current.has(itemKey)) {
                  const originalItem = allItemsRef.current.get(itemKey)
                  if (!originalItem.raw.triggerStateBenchmark) {
                    originalItem.raw.triggerStateBenchmark = []
                  }
                  originalItem.raw.triggerStateBenchmark.push(benchmark)
                }
              }
            }
          } else {
            // ECOSYSTEMS
            for (const item of mergedSelection) {
              let benchmark
              const hasBenchmark =
                Array.isArray(item?.raw?.triggerStateBenchmark) && item.raw.triggerStateBenchmark.length > 0
              if (hasBenchmark) {
                benchmark = item.raw.triggerStateBenchmark[0]
              } else {
                const payloadItem = {
                  site_state_id: Number(siteStateRecord.id),
                  season: item.raw?.season?.id || item.raw?.season
                }
                payloadItem.siteEcosystemId = item.raw?.id
                const estimate = item.raw?.benchmark_estimate || item.raw?.benchmarkEstimate
                if (estimate) {
                  payloadItem.benchmark_estimate = estimate
                }
                newBenchmarksPayload.push(payloadItem)
                itemsToUpdate.push(item)
              }
            }

            let createdBenchmarks = []
            if (newBenchmarksPayload.length > 0) {
              createdBenchmarks = await requestTriggerStateBenchmark(newBenchmarksPayload)
            }
            if (createdBenchmarks.length > 0) {
              for (let i = 0; i < createdBenchmarks.length; i++) {
                const benchmark = createdBenchmarks[i]
                const originalItem = itemsToUpdate[i]
                if (!originalItem.raw.triggerStateBenchmark) {
                  originalItem.raw.triggerStateBenchmark = []
                }
                originalItem.raw.triggerStateBenchmark.push(benchmark)
              }
            }
          }

          setSelectedByFilter((prev) => ({
            ...prev,
            [filterBy]: []
          }))
          setSelectedAllMap((prev) => ({
            ...prev,
            [getPageKey(biodiversityPagination.currentPage, filterBy)]: false
          }))
          setActiveTab('createEvaluation')
        } catch (err) {
          console.error('Benchmark create error:', err)
          showErrorToast('Failed to create benchmark.')
        }
      }
    },
    [
      deriveItemsFromIds,
      siteStateRecord?.id,
      requestTriggerStateBenchmark,
      requestTriggerStateBenchmarkTaxonGroups,
      items,
      filterBy,
      selectedGroupsByFilter
    ]
  )

  const handleViewMonitoringClick = useCallback(() => {
    setActiveTab('createEvaluation')
  }, [])

  return (
    <div>
      {initialLoad ? (
        <LoadingWrapper
          style={{ height: '100vh', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        >
          <Loader />
        </LoadingWrapper>
      ) : (
        <>
          {activeTab === 'biodiversity' && (
            <>
              <PageTitle>
                <FormattedMessage id="stateTitle" />
              </PageTitle>

              <PageSubtitleWrapper>
                <PageSubtitle>
                  <FormattedMessage id="stateSubHeading" />
                </PageSubtitle>
                <Tooltip
                  style={{ zIndex: 1000 }}
                  placement="bottom"
                  trigger={['hover']}
                  align={{ offset: [0, 16] }}
                  overlay={{
                    label: 'stateTitle',
                    infoTooltip: 'toolTipstateTitle'
                  }}
                  type="big"
                >
                  <TooltipHintWrapper>
                    <InfoIcon />
                  </TooltipHintWrapper>
                </Tooltip>
              </PageSubtitleWrapper>
              <ErrorWrapper>
                {!isSiteStateValid && incompleteBenchmarkIds?.length === 0 && (
                  <>
                    <ErrorIcon />
                    <FormattedMessage id="stateEvaluationValidation" />
                  </>
                )}
              </ErrorWrapper>

              <BiodiversitySelector
                items={items}
                handleSelectAll={handleSelectAll}
                handleToggleClass={handleToggleClass}
                isClassSelected={isClassSelected}
                isClassDisabled={isClassDisabled}
                onFilterChange={handleFilterChange}
                globalSelectAll={globalSelectAll}
                setGlobalSelectAll={setGlobalSelectAll}
                isGlobalSelectDisabled={isGlobalSelectDisabled}
                onActionClick={handleCreateEvaluation}
                onViewMonitoring={handleViewMonitoringClick}
                isLoadingEvaluations={loading}
                selected={selected}
                isSlectedAll={selectedAllMap[getPageKey()] || false}
                handleToggle={handleToggle}
                isEditable={isEditable}
                incompleteBenchmarkIds={incompleteBenchmarkIds}
                clearAllSelections={clearAllSelections}
                totalBenchmarks={totalBenchmarks}
                isAllAdded={isAllAdded}
                pagination={{
                  resultsPerPage: biodiversityPagination.resultsPerPage,
                  totalNumber: biodiversityPagination.totalNumber,
                  totalPages: biodiversityPagination.totalPages,
                  currentPage: biodiversityPagination.currentPage,
                  nextPage: handleBiodiversityNext,
                  prevPage: handleBiodiversityPrev,
                  onChange: handleBiodiversityPerPageChange,
                  selectPage: handleBiodiversitySelectPage
                }}
              />
            </>
          )}

          {activeTab === 'createEvaluation' && (
            <>
              <PageTitle>
                <FormattedMessage id="stateBiodiversityTitle" />
              </PageTitle>

              <PageSubtitleWrapper>
                <PageSubtitle>
                  <FormattedMessage id="stateBiodiversitysub" />
                </PageSubtitle>
                <Tooltip
                  style={{ zIndex: 1000 }}
                  placement="bottom"
                  trigger={['hover']}
                  align={{ offset: [0, 16] }}
                  overlay={{
                    label: 'stateBiodiversityTitle',
                    infoTooltip: 'stateBiodiversityTitleTooltip'
                  }}
                  type="big"
                >
                  <TooltipHintWrapper>
                    <InfoIcon />
                  </TooltipHintWrapper>
                </Tooltip>
              </PageSubtitleWrapper>

              <StateEvaluationSelector
                onBack={goBackToBiodiversity}
                onRemoveSelectedItem={handleRemoveSelectedItem}
                siteStateId={siteStateRecord?.id}
                incompleteBenchmarkIds={incompleteBenchmarkIds}
                isSiteStateValid={isSiteStateValid}
                siteId={siteId}
                isEditable={isEditable}
                isRevaluation={isRevaluation}
              />
            </>
          )}
        </>
      )}

      <ScrollToTop bottom="104px" />

      <Modal
        width="416px"
        isOpen={showTabWarning}
        toggleModal={() => setShowTabWarning(false)}
        modalTitleId="Attention"
        bottomBorder={true}
      >
        <RemoveItemModalText data-test="modal-body">
          <FormattedMessage
            id="addSpeciesBeforeEcosystems"
            values={{
              type: <b>{biodiversityFilter === 1 ? 'species' : 'ecosystem'}</b>,
              type2: <b>{biodiversityFilter === 1 ? 'ecosystem' : 'species'}</b>
            }}
          />
        </RemoveItemModalText>

        <RemoveItemModalFooter>
          <Button type="secondary" onClick={() => setShowTabWarning(false)}>
            <FormattedMessage id="okay" />
          </Button>
        </RemoveItemModalFooter>
      </Modal>
    </div>
  )
}

export default SiteMonitoringStatePage
